import * as React from "react";
import {api} from "../../../../api/api";
import {none, option, Option} from "ts-option";
import DataGrid from "../../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import Loading from "../../../ui/loading/Loading";
import DateUtils from "../../../../utils/DateUtils";
import FormGroup from "../../../../hiberUiKit/src/molecules/FormGroup/FormGroup";
import CreatePartnerBrandPointsModal from "./CreatePartnerBrandPointsModal";
import ButtonPrimary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import PartnerBrandPoints, {PartnerBrandPointsStatus} from "../../../../model/partnerBrand/PartnerBrandPoints";
import AuthenticatedEntity, {AuthenticatedEntityTypeEnum} from "../../../../model/user/AuthenticatedEntity";
import {getUser} from "../../../../store/view/user";
import {notifyBugsnagAndShowToast} from "../../../../utils/Utils";
import Toast from "../../../helper/Toast";
import PartnerBrandPointsCalculator from "./PartnerBrandPointsCalculator";
import PartnerBrandPointsSummary from "../../../../model/partnerBrand/PartnerBrandPointsSummary";
import "../../../v5/table/workspaceCardList.scss";

interface Props {
	/**
	 * Partner brand id
	 */
	partnerBrandId: Option<string>
	/**
	 * On add points
	 * @param {PartnerBrandPoints} point
	 */
	onAddPoints: (point: PartnerBrandPoints) => void
	/**
	 * Newly added points
	 */
	points: PartnerBrandPoints[]
}

interface State {
	points: Option<PartnerBrandPointsSummary[]>
	createPartnerBrandPointsModalVisible: boolean
	/**
	 * Indicates if the screen is a phone's, where points show as cards rather than as a table.
	 */
	isPhone: boolean
}

export default class PartnerBrandPointsFormGroup extends React.Component<Props, State> {
	phoneQuery: MediaQueryList | null = window.matchMedia ? window.matchMedia("(max-width: 640px)") : null;

	constructor(props: Props) {
		super(props);

		this.state = {
			points: none,
			createPartnerBrandPointsModalVisible: false,
			isPhone: !!this.phoneQuery && this.phoneQuery.matches
		};
		this.renderNewlyAddedPoints = this.renderNewlyAddedPoints.bind(this);
		this.updatePointStatus = this.updatePointStatus.bind(this);
		this.renderRow = this.renderRow.bind(this);
		this.renderCard = this.renderCard.bind(this);
	}

	componentWillUnmount() {
		if (this.phoneQuery) {
			this.phoneQuery.removeListener(this.onPhoneChange);
		}
	}

	onPhoneChange = (query: MediaQueryList) => this.setState({isPhone: query.matches});

	componentDidMount() {
		if (this.phoneQuery) {
			this.phoneQuery.addListener(this.onPhoneChange);
		}
		if (this.props.partnerBrandId.isDefined) {
			api.instance.partnerBrandApi.fetchPoints(this.props.partnerBrandId.get)
				.then((points) => {
					this.setState({points: option(PartnerBrandPointsCalculator.groupPartnerBrandPoints(points))})
				})
				.catch((e) => {notifyBugsnagAndShowToast(e, "Partner brand points fetch")})
		}
		else {
			this.setState({points: option([])});
		}
	}

	render() {
		return (
			<div className={"c-partner-brand-points-form-group"}>
				<Loading isLoading={this.state.points.isEmpty}>
					{this.state.points.map(points =>
						<FormGroup label={"Reward points"}>
							<ButtonPrimary label={"Add points"}
										   onClick={this.showCreatePartnerBrandPointsModal}/>
							{this.state.isPhone ?
							<ul className={"c-workspace-cards"}>
								{this.allSummaries(points).map(this.renderCard)}
							</ul> :
							<div className={"c-table-wrapper"}>
								<DataGrid>
									<thead>
									<tr>
										<th>Creator</th>
										<th>Creation date</th>
										<th>Description</th>
										<th>Quote reference number</th>
										<th>Earned</th>
										<th>Used</th>
										<th>Status</th>
										<th>Actions</th>
									</tr>
									</thead>
									<tbody>
									{this.renderNewlyAddedPoints()}
									{points.map(this.renderRow)}
									</tbody>
								</DataGrid>
							</div>}
						</FormGroup>
					).orNull}
				</Loading>
				{this.state.createPartnerBrandPointsModalVisible &&
				<CreatePartnerBrandPointsModal onClose={this.hideCreatePartnerBrandPointsModal}
											   onCreate={this.props.onAddPoints}/>}
			</div>
		)
	}

	renderNewlyAddedPoints() {
		if (this.props.points.length > 0) {
			return PartnerBrandPointsCalculator.groupPartnerBrandPoints(this.props.points).map(this.renderRow);
		}
		return null;
	}

	renderRow(p: PartnerBrandPointsSummary) {
		return (
			<tr key={p.id}>
				<td>{this.getCreatorName(p.creator)}</td>
				<td>{p.createdAt.format(DateUtils.DATE_TIME_FORMAT)}</td>
				<td>{p.description.orNull}</td>
				<td>{p.quoteReferenceNumber.orNull}</td>
				<td>
					{p.hasPendingValues && <div>{p.pendingAmount}</div>}
					{p.hasSubmittedValues && <div>{p.submittedAmount}</div>}
				</td>
				<td>
					{p.hasPendingValues && <div>{p.pendingUsedPoints}</div>}
					{p.hasSubmittedValues && <div>{p.submittedUsedPoints}</div>}
				</td>
				<td>
					{p.hasPendingValues && <div>PENDING</div>}
					{p.hasSubmittedValues && (p.notSaved ? <div>Update Partner Brand to save</div> : <div>SUBMITTED</div>)}
				</td>
				<td>{this.renderAcceptPointsButton(p)}</td>
			</tr>
		)
	}

	/**
	 * Points added on the page but not saved yet, then the saved ones, as the table lists them.
	 */
	allSummaries(points: PartnerBrandPointsSummary[]): PartnerBrandPointsSummary[] {
		return PartnerBrandPointsCalculator.groupPartnerBrandPoints(this.props.points).concat(points);
	}

	/**
	 * A card per entry on a phone, with what the table's columns say.
	 */
	renderCard(p: PartnerBrandPointsSummary) {
		return (
			<li key={p.id} className={"c-workspace-cards_card"} style={{cursor: "default"}}>
				<span className={"c-workspace-cards_main"}>
					<span className={"c-workspace-cards_title"}>{p.description.getOrElse("Reward points")}</span>
					<span className={"c-workspace-cards_line is-secondary"}>
						{`${this.getCreatorName(p.creator) || ""}, ${p.createdAt.format(DateUtils.DATE_TIME_FORMAT)}`}
					</span>
					{p.quoteReferenceNumber.map(ref =>
						<span className={"c-workspace-cards_line is-secondary"}>{`Quote ${ref}`}</span>).orNull}
				</span>
				<span className={"c-workspace-cards_aside"}>
					{p.hasPendingValues && <span className={"c-workspace-cards_status"}>PENDING</span>}
					{p.hasSubmittedValues &&
					<span className={"c-workspace-cards_status"}>{p.notSaved ? "Update Partner Brand to save" : "SUBMITTED"}</span>}
				</span>
				<span className={"c-workspace-cards_amounts"}>
					<span className={"c-workspace-cards_amount"}>
						<span className={"c-workspace-cards_label"}>Earned</span>
						{p.hasPendingValues && <span>{p.pendingAmount}</span>}
						{p.hasSubmittedValues && <span>{p.submittedAmount}</span>}
					</span>
					<span className={"c-workspace-cards_amount"}>
						<span className={"c-workspace-cards_label"}>Used</span>
						{p.hasPendingValues && <span>{p.pendingUsedPoints}</span>}
						{p.hasSubmittedValues && <span>{p.submittedUsedPoints}</span>}
					</span>
				</span>
				{p.hasPendingValues &&
				<span style={{gridColumn: "1 / -1"}}>{this.renderAcceptPointsButton(p)}</span>}
			</li>
		);
	}

	renderAcceptPointsButton = (p: PartnerBrandPointsSummary) => {
		if (p.hasPendingValues) {
			return (
				<ButtonPrimary label={"Accept points"} onClick={() => this.acceptPoints(p)}/>
			);
		}
		return null;
	};

	acceptPoints = (point: PartnerBrandPointsSummary) => {
		point.quoteId.map(quoteId => {
			api.instance.quoteApi.submitRewardPoints(quoteId).then(() => {
				this.updatePointStatus(point);
			}).catch((error) => this.handleAcceptError(error, point));
		}).getOrElse(() => {
			api.instance.partnerBrandApi.acceptPoint(point.id).then(() => {
				this.updatePointStatus(point);
			}).catch((error) => this.handleAcceptError(error, point));
		});
	};

	updatePointStatus(point: PartnerBrandPointsSummary) {
		const updatedPoints = this.state.points.getOrElse([]).map(
			(p: PartnerBrandPointsSummary) => p.id === point.id ? point.submitPendingPoints() : p
		);
		this.setState({points: option(updatedPoints)});
	};

	handleAcceptError(error: any, point: PartnerBrandPointsSummary) {
		if (error.response && error.response.status === 409) {
			this.updatePointStatus(point);
		}
		else if (error.response && error.response.status === 404) {
			Toast.error("Given points dont exits. Please refresh the page to see up to date points",
				"Change point status");
		}
		else {
			notifyBugsnagAndShowToast(error, "Change point status", "Cannot update points status")
		}
	}

	getCreatorName(creator: AuthenticatedEntity): string {
		if (creator.type === AuthenticatedEntityTypeEnum.User) {
			return getUser(creator.id).map(u => u.getDisplayName()).orNull
		}
		return creator.type;
	}

	showCreatePartnerBrandPointsModal = () => this.setState({createPartnerBrandPointsModalVisible: true});
	hideCreatePartnerBrandPointsModal = () => this.setState({createPartnerBrandPointsModalVisible: false});
}